import { useUiStrings } from '@/hooks/useUiStrings'
import type { UiStrings } from '@/constants/ui-locale'
import type { MeetingBriefAgenda } from '@ai-meeting/shared'
import { isAgendaEmpty, splitAgendaTopicBody } from './briefAgendaLine'

type LineVariant = 'topicBody' | 'plain'

function AgendaBulletLine({ text, variant }: { text: string; variant: LineVariant }) {
  if (variant === 'plain') {
    return (
      <li className="relative pl-3.5 text-xs leading-[19.5px] text-[#334155]">
        <span className="absolute left-0 text-[#94a3b8]" aria-hidden="true">
          •
        </span>
        {text}
      </li>
    )
  }

  const { topic, body } = splitAgendaTopicBody(text)
  return (
    <li className="relative pl-3.5 text-xs leading-[19.5px] text-[#334155]">
      <span className="absolute left-0 text-[#94a3b8]" aria-hidden="true">
        •
      </span>
      {topic ? (
        <>
          <span className="font-bold text-[#111827]">{topic}</span>
          {body ? <span>{body.startsWith(' ') ? body : ` ${body}`}</span> : null}
        </>
      ) : (
        text
      )}
    </li>
  )
}

function AgendaCategoryBlock({
  title,
  accentClass,
  barClass,
  items,
  lineVariant,
}: {
  title: string
  accentClass: string
  barClass: string
  items: string[]
  lineVariant: LineVariant
}) {
  if (items.length === 0) return null

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1.5">
        <span className={`h-3 w-[3px] shrink-0 rounded-[2px] ${barClass}`} aria-hidden="true" />
        <h4
          className={`text-[11px] font-bold uppercase tracking-[0.55px] leading-[17.88px] ${accentClass}`}
        >
          {title}
        </h4>
      </div>
      <ul className="flex flex-col gap-1 border-l-2 border-transparent pl-3.5">
        {items.map((text, index) => (
          <AgendaBulletLine
            key={`${title}:${index}:${text.slice(0, 24)}`}
            text={text}
            variant={lineVariant}
          />
        ))}
      </ul>
    </div>
  )
}

function sectionLabels(t: UiStrings) {
  return {
    progress: t.briefAgendaSectionProgress,
    decisions: t.briefAgendaSectionDecisions,
    questions: t.briefAgendaSectionQuestions,
    risks: t.briefAgendaSectionRisks,
  }
}

export default function BriefAgendaSection({ agenda }: { agenda: MeetingBriefAgenda }) {
  const { t } = useUiStrings()

  if (isAgendaEmpty(agenda)) return null

  const labels = sectionLabels(t)

  return (
    <section
      className="flex flex-col gap-3.5 rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-[17px] shadow-sm"
      data-testid="brief-agenda"
    >
      <div className="border-b border-[#f1f5f9] pb-3 dark:border-[var(--border-color)]">
        <h3 className="text-sm font-bold leading-5 text-[#111827] dark:text-[var(--text-primary)]">
          {t.briefAgendaTitle}
        </h3>
      </div>
      <div className="flex flex-col gap-4">
        <AgendaCategoryBlock
          title={labels.progress}
          accentClass="text-[#1f2937]"
          barClass="bg-[#1f2937]"
          items={agenda.progress}
          lineVariant="topicBody"
        />
        <AgendaCategoryBlock
          title={labels.decisions}
          accentClass="text-[#7e22ce]"
          barClass="bg-[#7e22ce]"
          items={agenda.decisions}
          lineVariant="topicBody"
        />
        <AgendaCategoryBlock
          title={labels.questions}
          accentClass="text-[#2563eb]"
          barClass="bg-[#2563eb]"
          items={agenda.questions}
          lineVariant="plain"
        />
        <AgendaCategoryBlock
          title={labels.risks}
          accentClass="text-[#dc2626]"
          barClass="bg-[#dc2626]"
          items={agenda.risks}
          lineVariant="plain"
        />
      </div>
    </section>
  )
}
